import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import {
  DOC_TYPE_TO_SCREEN,
  OP_DOCUMENTS_DISPLAY_MODE,
  OPERATOR_PANEL_INFORMATION_SCREENS,
} from '../../../../operatorPanel/constants/operatorPanel.constants';
import { decrementTabCount, incrementTabCount } from '@reducers/operatorPanel';
import { navigationRef } from '@helpers';
import { DocType } from '@v2/components/imageHandling/types/types.d';
import { useAppDispatch } from '@store/hooks/useAppDispatch';

export const useTabCount = () => {
  const dispatch = useAppDispatch();
  const route = navigationRef?.current?.getCurrentRoute();

  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();

  const onSuccessDocTypeChange = (newDocType: DocType, prevDocType: DocType) => {
    if (!safeIsOperatorPanelInfoScreen) {
      return;
    }
    const screenByNewDocType = DOC_TYPE_TO_SCREEN[newDocType];
    const screenByPrevDocType = DOC_TYPE_TO_SCREEN[prevDocType];

    if (screenByPrevDocType) {
      dispatch(decrementTabCount({ tabName: screenByPrevDocType as OPERATOR_PANEL_INFORMATION_SCREENS }));
    }

    if (screenByNewDocType) {
      dispatch(incrementTabCount({ tabName: screenByNewDocType as OPERATOR_PANEL_INFORMATION_SCREENS }));

      const navParams = {
        mode: OP_DOCUMENTS_DISPLAY_MODE.GALLERY,
        selectedImageKey: route?.params?.selectedImageKey,
        ...(route?.params?.scheduledProductionOperationId && { scheduledProductionOperationId: route?.params?.scheduledProductionOperationId }),
        ...(route?.params?.id && { id: route?.params?.id }),
        ...(route?.params?.selectedProductionOrderId && { selectedProductionOrderId: route?.params?.selectedProductionOrderId })
      };

      // Navigate to the new document type screen
      // The image update is already handled in OperatorPanelInformationDocuments before this callback
      navigationRef?.current?.navigate(OPERATOR_PANEL_INFORMATION_SCREENS.Information, {
        screen: screenByNewDocType,
        params: navParams,
      });
    }
  };

  const onSuccessImageUpload = () => {
    incrementTabCount({ tabName: route?.name as OPERATOR_PANEL_INFORMATION_SCREENS });
    if (route?.name && route?.name !== OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments) {
      incrementTabCount({ tabName: OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments as OPERATOR_PANEL_INFORMATION_SCREENS });
    }

    const displayMode = route?.params?.mode;
    const shouldChangeDisplayMode = displayMode === OP_DOCUMENTS_DISPLAY_MODE.TILE;
    navigationRef.current?.setParams({ ...(shouldChangeDisplayMode ? { mode: OP_DOCUMENTS_DISPLAY_MODE.GALLERY } : {}) });
  };

  const onSuccessImageRemove = (docType: DocType) => {
    const screenByDocType = DOC_TYPE_TO_SCREEN?.[docType];
    decrementTabCount({ tabName: route?.name as OPERATOR_PANEL_INFORMATION_SCREENS });

    if (screenByDocType && screenByDocType !== route.name) {
      decrementTabCount({ tabName: screenByDocType as OPERATOR_PANEL_INFORMATION_SCREENS });
    }

    if (screenByDocType !== OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments) {
      decrementTabCount({ tabName: OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments as OPERATOR_PANEL_INFORMATION_SCREENS });
    }
  };

  return { onSuccessDocTypeChange, onSuccessImageRemove, onSuccessImageUpload };
};
